// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { Option } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import color from "@rugby/XDL/PreStyle/colors";
import { color as getColorWithOpacity } from "@rugby/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

/**
 * This is a helper method that turn the colors dictionary into dropdown options
 * @param colors
 */
function getAllColorsAsDropdownOptions(colors: any): Option<string>[] {
    const options: Option<string>[] = [];
    for (const key in colors) {
        for (const subkey in colors[key]) {
            if (subkey.toLowerCase().indexOf("contrast") === -1) {
                options.push({ displayName: `${key}.${subkey}`, value: `${key}.${subkey}` });
            }
        }
    }
    return options;
}

const enum fields {
    paddingSize = "paddingSize",
    marginSize = "marginSize",
    paddingSizeInput = "paddingSizeInput",
    horizontalRule = "horizontalRule",
    hrHeight = "hrHeight",
    hrColor = "hrColor",
    colorAlpha = "colorAlpha",
}

interface SpacerProps extends WidgetProps {
    fields: {
        [fields.marginSize]: string;
        [fields.paddingSize]: string;
        [fields.paddingSizeInput]: string;
        [fields.horizontalRule]: boolean;
        [fields.hrHeight]: number;
        [fields.hrColor]: string;
        [fields.colorAlpha]: number;
    };
}

const customValueText = "customValue";

const getPaddingSize = (fields: any) => {
    if (!fields.paddingSize) {
        return 0;
    }

    return fields.paddingSize !== customValueText ? Number(fields.paddingSize) : Number(fields.paddingSizeInput);
};

const spacerTemplateWidget: React.FC<SpacerProps> = props => {
    const paddingSize = getPaddingSize(props.fields);

    let horizontalRule;

    if (props.fields.horizontalRule) {
        // do some validation on the hrHeight Value (1 <= hrHeight <= props.fields.paddingSize)
        let hrHeight = props.fields.hrHeight;
        if (!hrHeight || hrHeight < 1) {
            hrHeight = 1;
        } else if (hrHeight > paddingSize) {
            hrHeight = paddingSize;
        }

        horizontalRule = css`
            &:after {
                content: "";
                display: block;
                width: 100%;
                height: ${hrHeight}px;
                background-color: ${getColorWithOpacity(props.fields.hrColor, props.fields.colorAlpha || 1)};
            }
        `;
    }

    const cssBlock = css`
        height: ${paddingSize}px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        margin: ${props.fields.marginSize || 0};

        ${horizontalRule}
    `;

    return <StyledWrapper css={cssBlock} className="spacer"></StyledWrapper>;
};

export const definition: WidgetDefinition = {
    group: "Xngage Custom" as any,
    fieldDefinitions: [
        {
            name: fields.marginSize,
            displayName: "Margin Size",
            editorTemplate: "TextField",
            fieldType: "General",
            defaultValue: "0px",
            isRequired: false,
            placeholder: "Enter: 5px or any margin style",
            validate: (value: string | null, item: HasFields) => {
                if (!value) {
                    return `A value must be provided.`;
                }
                return CSS.supports("margin", value) ? null : `Invalid Input`;
            },
        },
        {
            name: fields.paddingSize,
            displayName: "Padding Size",
            editorTemplate: "DropDownField",
            options: [
                {
                    displayName: "25px",
                    value: "25",
                },
                {
                    displayName: "50px",
                    value: "50",
                },
                {
                    displayName: "75px",
                    value: "75",
                },
                {
                    displayName: "100px",
                    value: "100",
                },
                {
                    displayName: "Custom value",
                    value: customValueText,
                },
            ],
            isRequired: true,
            defaultValue: "50",
            fieldType: "General",
        },
        {
            name: fields.paddingSizeInput,
            displayName: "Padding Size - Input",
            editorTemplate: "IntegerField",
            defaultValue: 0,
            fieldType: "General",
            placeholder: "add Padding Size",
            isRequired: false,
            min: 1,
            isVisible: (item: HasFields) => item.fields.paddingSize === customValueText,
        },
        {
            name: fields.horizontalRule,
            displayName: "Show Horizontal Rule",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
        },
        {
            name: fields.hrColor,
            displayName: "Rule Color",
            editorTemplate: "DropDownField",
            options: getAllColorsAsDropdownOptions(color),
            isRequired: true,
            defaultValue: "common.ui4",
            fieldType: "General",
            isVisible: (item: HasFields) => item.fields.horizontalRule,
        },
        {
            name: fields.colorAlpha,
            displayName: "Color - Opacity",
            editorTemplate: "TextField",
            defaultValue: "1",
            fieldType: "General",
            placeholder: "add color opacity",
            isRequired: true,
            validate: (value: string | null, item: HasFields) => {
                if (!value) {
                    return `A value must be provided.`;
                }
                return isNumeric(value) && parseFloat(value) <= 1 && parseFloat(value) > 0 ? null : `Invalid Input`;
            },
            isVisible: (item: HasFields) => item.fields.horizontalRule,
        },
        {
            name: fields.hrHeight,
            displayName: "Rule Height",
            editorTemplate: "IntegerField",
            defaultValue: 1,
            fieldType: "General",
            placeholder: "ُEnter rule height in px",
            isRequired: true,
            min: 1,
            validate: (value: number | null, item: HasFields) => {
                const paddingSize = getPaddingSize(item.fields);
                if (!value || value > Number(paddingSize)) {
                    return `Max value is ${item.fields.paddingSize}`;
                }
                return null;
            },
            isVisible: (item: HasFields) => item.fields.horizontalRule,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: spacerTemplateWidget,
    definition,
};

export default widgetModule;
